import { PaletteColorOptions, createTheme } from "@mui/material/styles";
import * as colors from "./colors";

export const DEFAULT_SPACING = 2;
export const SPACING_BASE_PIXELS = 8;

export * from "./colors";

type BorderPalette = {
  subtle: string;
  warm: string;
};

type StatusPalette = {
  active: string;
  scheduled: string;
  paused: string;
};

declare module "@mui/material" {
  interface ButtonPropsVariantOverrides {
    primary: true;
    ghost: true;
    naked: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    olive: Palette["primary"];
    lightText: Palette["text"];
    border: BorderPalette;
    status: StatusPalette;
  }
  interface PaletteOptions {
    olive?: PaletteColorOptions;
    lightText?: Palette["text"];
    border?: BorderPalette;
    status?: StatusPalette;
  }
}

export const olive = {
  light: colors.oliveLighter,
  extraLight: colors.oliveLightest,
  main: colors.olive,
  dark: colors.oliveDarker,
  contrastText: colors.white,
};

export const blackShades = {
  main: colors.black,
  light: colors.blackLightest,
  contrastText: colors.white,
};

const warningShades = {
  light: colors.warningLight,
  main: colors.warningMedium,
  dark: colors.warningDark,
};

const statusShades = {
  active: colors.successDark,
  scheduled: colors.indigo,
  paused: colors.warningDark,
};

const errorShades = {
  light: colors.errorMedium,
  main: colors.errorDark,
};

export const monospaced = Object.freeze({
  fontFamily: "monospace",
});

const contentFont = "GT America Regular, Helvetica, Arial, sans-serif";
const headlineFont = "GT America Condensed Regular, Helvetica, Arial, sans-serif";

export const theme = createTheme({
  palette: {
    primary: blackShades,
    olive,
    warning: warningShades,
    error: errorShades,
    //info : {}
    text: {
      primary: colors.black,
      secondary: blackShades.light,
      disabled: colors.whiteDarker,
    },
    border: {
      subtle: colors.lightGrayLighter,
      warm: colors.cream,
    },
    status: statusShades,
    lightText: {
      primary: colors.white,
      secondary: colors.whiteDarker,
      disabled: colors.whiteDark,
    },
  },

  typography: {
    fontFamily: contentFont,
    h1: { fontFamily: headlineFont, fontWeight: 100 },
    h2: { fontFamily: headlineFont, fontWeight: 100 },
    h3: { fontFamily: headlineFont, fontWeight: 100 },
    h4: { fontFamily: headlineFont, fontWeight: 100 },
    h5: { fontFamily: headlineFont, fontWeight: 100 },
    h6: { fontFamily: headlineFont, fontWeight: 100 },
  },

  components: {
    MuiAppBar: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: colors.lightGray,
          color: colors.black,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          margin: 16,
        },
        paperScrollPaper: {
          maxHeight: `calc(100% - ${16 * 2}px)`,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: { margin: "16px 0" }, // Add the same style as in expanded mode
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardWarning: {
          backgroundColor: `${warningShades.light}20`,
        },
        standardError: {
          backgroundColor: `${errorShades.light}20`,
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { size: "large" },
          style: { fontSize: 18, height: 50 },
        },
        {
          props: { size: "medium" },
          style: { fontSize: 16, height: 40 },
        },
        {
          props: { size: "small" },
          style: { fontSize: 14, height: 30 },
        },
        {
          props: { variant: "primary" },
          style: {
            background: colors.black,
            color: colors.white,
            "&:hover": {
              backgroundColor: colors.darkBackground,
              color: colors.lightGrayFont,
            },
          },
        },
        {
          props: { variant: "naked" },
          style: {
            border: `1px solid ${colors.lightGrayFont}`,
            "&:hover": {
              backgroundColor: colors.white,
              border: `1px solid ${colors.black}`,
            },
          },
        },
        {
          props: { variant: "ghost" },
          style: {
            backgroundColor: "none",
            borderWidth: 0,
            border: "1px solid transparent",
            "&:hover": {
              backgroundColor: colors.white,
              border: `1px solid ${colors.black}`,
            },
          },
        },
        {
          props: { disabled: true },
          style: {
            backgroundColor: colors.disabledGray,
            borderWidth: 0,
            "&:disabled": {
              color: colors.white,
            },
          },
        },
      ],
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            "&.Mui-focused fieldset": {
              border: `1px solid`,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "1px solid",
          },
        },
      },
    },
  },
});

export default theme;
